/* eslint-disable lingui/no-unlocalized-strings */
import { colors } from '@global/constants'
import { stepIconClasses, tabClasses, tableRowClasses } from '@mui/material'
import { createTheme } from '@mui/material/styles'

export function getTheme(mode) {
  const theme = createTheme({
    palette: {
      mode: mode ? 'dark' : 'light',
      primary: {
        light: colors.blue.light,
        main: colors.blue.main,
        dark: colors.blue.dark,
        contrastText: colors.white,
      },
      gray: {
        main: '#CCCCCC',
        light: '#8A8A8A',
        dark: '#B1BDC0',
        blueGray: colors.blue.smokey_light,
      },
      secondary: {
        light: '#18D486',
        main: '#4CAF50',
        dark: '#147853',
        contrastText: colors.white,
      },
      background: {
        paper: colors.white,
        main: colors.white,
        light: colors.gray.light,
      },
      error: {
        light: '#FF5151',
        main: colors.red.main,
        dark: colors.red.dark,
        contrastText: 'white',
      },
      success: {
        main: '#4CAF50',
        dark: '#3A873F',
        contrastText: 'white',
      },
      warning: {
        light: '#FFC13C',
        main: colors.orange.light,
        dark: '#EC7032',
      },
      info: {
        light: colors.blue.light,
        main: colors.blue.main,
        dark: colors.blue.dark,
      },
    },
    typography: {
      useNextVariants: true,
      fontFamily: 'noto-sans, sans-serif',
      button: {
        textTransform: 'none',
      },
    },
    text: {
      light: '#000',
      dark: colors.white,
      gray: '#8A8A8A',
    },
    background: {
      light: colors.white,
      dark: '#000',
    },
    icons: {
      light: 'red',
      dark: 'green',
    },
    misc: {
      shadow: {
        boxShadow: `0 3px 10px ${colors.blue.smokey}`,
      },
    },
    components: {
      MuiTypography: {
        styleOverrides: {
          h1: {
            color: '#161A1A',
            fontSize: '35px',
            fontWeight: 'bold',
          },
          h2: {
            color: '#161A1A',
            textTransform: 'capitalize',
            fontSize: '27px',
            fontWeight: 'bold',
            letterSpacing: 0.5,
          },
          h3: {
            color: '#2E3C3E',
            textTransform: 'capitalize',
            fontSize: '23px',
            fontWeight: '600',
          },
          h4: {
            color: '#161A1A',
            textTransform: 'capitalize',
            fontSize: '13px',
            fontWeight: 'bold',
          },
          h5: {
            color: '#2E3C3E',
            fontSize: '12px',
          },
          h6: {
            color: '#000000',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            textTransform: 'capitalize',
            fontWeight: 'bold',
          },
          subtitle1: {
            color: '#2E3C3E',
            display: 'flex',
            alignItems: 'center',
            fontSize: '18px',
          },
          subtitle2: {
            color: '#8A8A8A',
            display: 'flex',
            fontSize: '12px',
            textTransform: 'uppercase',
            letterSpacing: 2,
            marginBottom: '-1px !important',
          },
          body1: {
            color: '#2e3c3e',
            alignItems: 'center',
            fontSize: '14px',
          },
          body2: {
            color: '#9C9C9C',
            fontSize: '11.5px',
          },
          overline: {
            letterSpacing: 2,
            fontSize: '14.17px',
            color: '#161A1A',
            marginBottom: '-5px !important',
          },
          caption: {
            color: '#8A8A8A',
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          size: 'small',
        },
      },
      MuiAlertTitle: {
        styleOverrides: {
          root: {
            color: colors.red.darker,
            fontWeight: 'bold',
            margin: '0 0 4px 0',
            lineHeight: '1.65',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            backgroundColor: colors.gray.gray200,
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          label: {
            color: colors.blue.smokey_light,
            fontSize: '0.8125rem',
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: ({ ownerState }) => {
            if (ownerState?.variant === 'genAiTooltip') {
              return {
                maxWidth: '200px',
                padding: '15px',
                border: '1px solid #8052FF',
                background: 'linear-gradient(45deg, #f6f2ff, #fdf8ff)',
                filter: 'drop-shadow(0px 3px 5px rgba(0, 0, 0, 0.4))',
                color: '#000000',
              }
            }

            if (ownerState?.variant === 'white') {
              return {
                background: '#FFFFFF',
                color: '#000000',
                fontSize: '.9rem !important',
                filter: 'drop-shadow(0px 3px 5px rgba(0, 0, 0, 0.4))',
              }
            }

            return {
              fontSize: '.9rem !important',
            }
          },
          arrow: ({ ownerState }) => {
            if (ownerState?.variant === 'genAiTooltip') {
              return {
                width: '3em',
                height: '1.71em',
                marginTop: '-1.71em !important',

                '&:before': {
                  border: '1px solid #8052FF',
                  background: 'linear-gradient(45deg, #f6f2ff, #fdf8ff)',
                },
              }
            }

            if (ownerState?.variant === 'white') {
              return {
                width: '2em',
                height: '1.31em',
                marginTop: '-1.31em !important',

                '&:before': {
                  border: '1px solid #FFFFFF',
                  background: '#FFFFFF',
                },
              }
            }
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            width: '100%',
            maxWidth: '100%',
            [`&.${tabClasses.selected}`]: {
              backgroundColor: '#ecedee !important',
              color: '#1f1f1f',
            },
            '&.MuiButtonBase-root': {
              alignItems: 'flex-start',
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            left: 0,
            borderLeft: `5px solid ${colors.teal.darker}`,
            backgroundColor: 'transparent',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            [`&.${tableRowClasses.selected}, &.${tableRowClasses}:hover`]: {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            '&:hover, &:focus, &:active': {
              boxShadow: 'none',
            },
          },
        },
      },
      MuiStepIcon: {
        styleOverrides: {
          root: {
            [`&.${stepIconClasses.completed}`]: {
              color: colors.green.main,
            },
            [`&.${stepIconClasses.active}`]: {
              color: colors.blue.main,
            },
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            boxShadow: '0px 0px 20px rgba(0, 0, 0, 0.15)',
          },
        },
      },
    },
  })
  return theme
}
